import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import ratioTravelObject from '@/entities/ratioTravel/interface'

import {
  ALL_PRICINGS_QUERY,
  SINGLE_PRICING_QUERY,
  PRICINGS_DEPENDENTS_QUERY,
  CREATE_PRICING_MUTATION,
  UPDATE_PRICING_MUTATION,
  DELETE_PRICING_MUTATION,
  FORCE_DELETE_PRICING_MUTATION,
  ADD_PRICING_TRAVELS_MUTATION,
  REMOVE_PRICING_TRAVELS_MUTATION,
  CREATE_PRICING_TRAVELS_MUTATION,
  DELETE_PRICING_TRAVELS_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const travelPricingRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_PRICING_QUERY, variables).then((data: any) => {
      return hydrateObject(data.pricing)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_PRICINGS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.pricings)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_PRICING_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.pricing),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_PRICINGS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.pricings),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(PRICINGS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([PRICINGS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_PRICING_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.pricing)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_PRICING_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.pricing)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_PRICING_MUTATION, variables).then((data: any) => {
      return data.deleteTravelPricing
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_PRICING_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTravelPricing
    })
  },

  assignTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PRICING_TRAVELS_MUTATION, variables)
  },

  unassignTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PRICING_TRAVELS_MUTATION, variables)
  },

  createTravels: async (id: string, items: Array<ratioTravelObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travels: items,
      },
    }
    return graphQLClient.request(CREATE_PRICING_TRAVELS_MUTATION, variables)
  },

  deleteTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PRICING_TRAVELS_MUTATION, variables)
  },
}

export default travelPricingRepository
